import React from 'react';
import { Empty, Spin, Button, Row, Col } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import OrderCard from './OrderCard';

const OrderCardList = ({
  orders = [],
  loading = false,
  isAdmin = false,
  groupByDate = false,
  pagination = null,
  onViewDetails,
  onEditItems,
  onEditDateTime,
  onCaptureSavedCard,
  onNoteSaved,
  onPageChange,
  CalendarIcon,
  showSchedulingWarnings = false
}) => {
  // Format order date groups for display
  const getOrdersGroupedByDate = () => {
    if (!groupByDate) return null;
    
    const grouped = {};
    
    orders.forEach(order => {
      let dateKey = 'No Date';
      
      if (order.property_date) {
        dateKey = order.property_date;
      }
      
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      
      grouped[dateKey].push(order);
    });
    
    // Sort dates in descending order
    return Object.keys(grouped)
      .sort((a, b) => {
        if (a === 'No Date') return 1;
        if (b === 'No Date') return -1;
        return new Date(b) - new Date(a);
      })
      .map(date => ({
        date,
        displayDate: date === 'No Date' ? 'No Date' : formatDisplayDate(date),
        orders: grouped[date]
      }));
  };
  
  // Format date for display
  const formatDisplayDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Render a single order card
  const renderOrderCard = (order) => (
    <Col xs={24} sm={24} md={12} lg={8} xl={6} key={order.id}>
      <OrderCard
        order={order}
        onViewDetails={onViewDetails}
        onEditItems={onEditItems}
        onEditDateTime={onEditDateTime}
        onCaptureSavedCard={onCaptureSavedCard}
        onNoteSaved={onNoteSaved}
        isAdmin={isAdmin}
        CalendarIcon={CalendarIcon}
        showSchedulingWarnings={showSchedulingWarnings}
      />
    </Col>
  );
  
  // Render loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spin size="large" />
      </div>
    );
  }
  
  // Render empty state
  if (!orders || orders.length === 0) {
    return (
      <Empty description="No orders found" className="my-12" />
    );
  }
  
  // Render grouped view
  if (groupByDate) {
    const groupedOrders = getOrdersGroupedByDate();
    
    return (
      <div>
        {groupedOrders.map(group => (
          <div key={group.date} className="mb-8">
            <h2 className="text-lg sm:text-xl font-bold mb-4 pb-2 border-b flex items-center">
              <CalendarOutlined className="mr-2" />
              {group.displayDate} ({group.orders.length})
            </h2>
            
            <Row gutter={[16, 16]}>
              {group.orders.map(order => renderOrderCard(order))}
            </Row>
          </div>
        ))}
        
        {/* Pagination controls */}
        {pagination && (
          <div className="mt-6 flex justify-center">
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-center mt-4">
              <Button
                disabled={pagination.current === 1}
                onClick={() => onPageChange(pagination.current - 1)}
              >
                Previous
              </Button>
              <div className="mx-0 sm:mx-4 flex items-center justify-center">
                Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
              </div>
              <Button
                disabled={pagination.current === Math.ceil(pagination.total / pagination.pageSize)}
                onClick={() => onPageChange(pagination.current + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Render standard grid view
  return (
    <div>
      <Row gutter={[16, 16]}>
        {orders.map(order => renderOrderCard(order))}
      </Row>
      
      {/* Pagination controls */}
      {pagination && (
        <div className="mt-6 flex justify-center">
          <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-center mt-4">
            <Button
              disabled={pagination.current === 1}
              onClick={() => onPageChange(pagination.current - 1)}
            >
              Previous
            </Button>
            <div className="mx-0 sm:mx-4 flex items-center justify-center">
              Page {pagination.current} of {Math.ceil(pagination.total / pagination.pageSize)}
            </div>
            <Button
              disabled={pagination.current === Math.ceil(pagination.total / pagination.pageSize)}
              onClick={() => onPageChange(pagination.current + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCardList;
